/**
 * @module mele
 * @description Mosy web custom elements library
 * - imports all Mosy custom web elements
 * - creates wrappers for loggin methods: this.info, this.debug, this.warn, this.error
 * - creates methods for DOM manipulation - creation of elements and finding elements
 * - creates helper methods for creation/deletion of modal dialogs
 * - extends *all* HTML elements with couple of helper methods:
 * .props - getter/setter to get/set property/attribute values for the element
 * .js - getter/setter to get/set tag, property/attribute values and children for the element
 * .json - setter/getter like .js but returns/accept JSON string
 *
 */

let mele_lang = 'en'
const DEFAULT_MODAL_KEY = ':modal'
const DEFAULT_POPUP_KEY = ':popup'

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * Fast creation of element using cloning templates
 * @param {string} tag - name of tag for creation
 * @returns {HTMLElement} - created element from tag
 */
Element.prototype.createElementFast = function(tag) {
  const doc = this.ownerDocument
  if(!doc.mosy_element_templates) doc.mosy_element_templates = {}
  let el_template = doc.mosy_element_templates[tag]
  if(!el_template) {
    el_template = doc.mosy_element_templates[tag] = tag==='svg' ? doc.createElementNS('http://www.w3.org/2000/svg', tag) : doc.createElement(tag)
  }
  const el = el_template.cloneNode()
  return el
} // createElementFast

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * Fast creation of element using cloning templates
 * @param {string} tag - name of tag for creation
 * @returns {HTMLElement} - created element from tag
 */
Document.prototype.createElementFast = function(tag) {
  tag = String(tag??'').toLocaleUpperCase()
  if(!tag) return
  const doc = this
  if(!doc.mosy_element_templates) doc.mosy_element_templates = {}
  let el_template = doc.mosy_element_templates[tag]
  if(!el_template) {
    if(tag==='SVG') {
      el_template = doc.createElementNS('http://www.w3.org/2000/svg', tag)
    }else {
      el_template = doc.createElement(tag)
    }
    doc.mosy_element_templates[tag] = el_template
  }
  return el_template.cloneNode()
} // createElementFast

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * Fast creation of element using cloning templates in document fragment
 * @param {string} tag - name of tag for creation
 * @returns {HTMLElement} - created element from tag
 */
DocumentFragment.prototype.createElementFast = function(tag) {
  tag = String(tag??'').toLocaleUpperCase()
  if(!tag) return
  const doc = this.ownerDocument || document
  if(!doc.mosy_element_templates) doc.mosy_element_templates = {}
  let el_template = doc.mosy_element_templates[tag]
  if(!el_template) {
    if(tag==='SVG') {
      el_template = doc.createElementNS('http://www.w3.org/2000/svg', tag)
    }else {
      el_template = doc.createElement(tag)
    }
    doc.mosy_element_templates[tag] = el_template
  }
  return el_template.cloneNode()
} // createElementFast

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * obtain Mosy module to which we belong
 * @returns {HTMLElement} - searched mele-modal or mele-window
 */
Element.prototype.getModule = function() {
  const holder = this.closest('.mele-modal,.mele-window')
  return holder?.module
} // getModule

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * obtain Mosy module to which we belong
 * @returns {string} - uppercase tagName of element
 */
Element.prototype.signature = function() {
  return this.tagName
} // signature

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * wrapper arround debug logger - yellow text
 */
Element.prototype.debug = function(...args) {
  const module = this.getModule()
  if(module) module.debug(this.signature()+':', ...args)
  else console.debug(this.signature()+':', ...args)
} // debug

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * wrapper arround info logger - green background of text
 */
Element.prototype.info = function(...args) {
  const module = this.getModule()
  if(module) module.info(this.signature()+':', ...args)
  else console.info(this.signature()+':', ...args)
} // info

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * wrapper arround warn logger - yellow background of text
 */
Element.prototype.warn = function(...args) {
  const module = this.getModule()
  if(module) module.warn(this.signature()+':', ...args)
  else console.warn(this.signature()+':', ...args)
} // warn

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * wrapper arround error logger - red background of text
 */
Element.prototype.error = function(...args) {
  const module = this.getModule()
  if(module) module.error(this.signature()+':', ...args)
  else console.error(this.signature()+':', ...args)
} // error

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * function to split string with custom separator
 * @param {string} separator - separator used for split
 * @returns {string[]} - array of strings split by separator
 */
// eslint-disable-next-line no-extend-native
String.prototype.splitCorrect = function(separator) {
  if(this==='') {
    return []
  } else {
    return this.split(separator)
  }
} // splitCorrect

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * helper function for finding child element(s) matching the selector using provided method
 * it's main role is to overcome problem with > operator at the beginning of selector
 * @param {string} selector - valid HTML querySelector
 * @param {string} queryMethod - string method used for selection
 * @returns {(HTMLElement|HTMLElement[])} - single HTMLElement or array of HTMLElements
 */
Element.prototype._findUsingMethod = function(selector, queryMethod) {
  const old = selector
  selector = selector
    .replace(/@\{\}/g, ':not([key])')
    .replace(/@\{([^}]*)\}/g, (m, p1)=>'[key="'+p1.replace(/@/g, '∂')+'"]')
    .replace(/@(\w*)/g, '[key="$1"]')
    .replace(/∂/g, '@')
  if(/(^|,)\s*>/.test(selector)) {
    const orig_id = this.id // remember current element id
    if(!this.id) { this.id = 'TMPID_'+Date.now() }
    selector = selector.replace(/(^|,)\s*>\s*/g, '$1#'+this.id+'>')
    try{
      const result = this[queryMethod](selector)
      if(orig_id) {
        this.id = orig_id
      }else {
        this.removeAttribute('id')
      }
      return result
    }catch(e) {
      console.error(e.message, old, selector)
      return []
    }
  }else{
    let result
    try{
      result = this[queryMethod](selector)
    }catch(e) {
      console.error(e)
    }
    return result
  }
} // _findUsingMethod

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * finds child element matching the selector
 * selector with @ at the beginning has special meaning - child with specified key attribute
 * there is a special hack to allow selector to start with >
 * @param {string} selector - valid HTML querySelector or "@key" selector
 * @return {(HTMLElement|undefined)} - HTMLElement based on querySelector or undefined if no match in DOM
 */
Element.prototype.find = function(selector) {
  return this._findUsingMethod(selector, 'querySelector')
} // find

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * finds all child elements matching the selector
 * selector with @ at the beginning has special meaning - child with specified key attribute
 * there is a special hack to allow selector to start with >
 * @param {string} selector - valid HTML querySelector or "@key" selector
 * @return {(HTMLElement[]|[])} - HTMLElement Array based on querySelector or empty array if no match in DOM
 */
Element.prototype.findAll = function(selector) {
  // querySelectorAll does not return real array, so transform it (to be able to use e.g. .forEach())
  const nodes = this._findUsingMethod(selector, 'querySelectorAll') || []
  return [...nodes]
} // findAll

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * calls method(el) for each child element matching the selector
 * @param {string} selector - valid HTML querySelector or "@key" selector
 * @param {function} method - callback method used to manipulate return value of selector (HTMLElement [])
 */
Element.prototype.forAll = function(selector, method) {
  this.findAll(selector).forEach(method)
} // forAll

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * create modal view from json definition of components
 * @param {(Object|JSON)} json - definition of module structure
 * @example
 * this.modalOpen({
 *   key: "example",
 *   header: "Example",
 *    children: [...]
 * })
 * @returns {mele-modal}
 */
Element.prototype.modalOpen = function(json) {
  json = {
    tag: 'mele-modal',
    key: DEFAULT_MODAL_KEY,
    ...json,
    invoker: this,
  }

  const key = String(json.key??'')

  // remove existing modal with the same key
  const body = this.ownerDocument.body
  const existing = body.findAll(`>.mele-modal@{${key}}, >.mele-modalwindow@{${key}}`)
  for(const el of existing) {
    el.deleteElement()
  }

  const { children, items, ...core } = json

  // create empty modal element
  const modal = body.appendChildren(core)

  // setup non-attribute properties
  // before adding children, so they can already access them
  // on startup
  const owner = this.closest('.mele-modal,.mele-window')
  const ref = owner.getAttribute('mod') || owner.getAttribute('modal')
  modal.setAttribute('modal', ref)
  modal.module = owner.module
  modal.publishToModule = owner.publishToModule
  modal.publishTo = owner.publishTo
  modal.publishOpen = owner.publishOpen

  // add children and items
  if(children) modal.appendChildren(children)
  if(items) modal.setItems(items)

  // setup visual attributes after creating children
  // so they obtain update of these attributes
  modal.behavior = owner.behavior
  modal.theme = owner.theme

  return modal
} // modalOpen

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * finds modal box
 * @param {(string)} key - id of modal
 * @returns {mele-modal}
 */
Element.prototype.modalFind = function(key) {
  key = String(key??'')
  if(key==='') {
    return this.closest('.mele-modal,.mele-modalwindow')
  }else{
    const body = this.ownerDocument.body
    if(!body) return
    return body.find(`>.mele-modal@{${key}}, >.mele-modalwindow@{${key}}`)
  }
} // modalFind

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * find modal box
 */
Element.prototype.modalFindDefault = function() {
  return this.ownerDocument.body.find(`>.mele-modal@{${DEFAULT_MODAL_KEY}}, >.mele-modalwindow@{${DEFAULT_MODAL_KEY}}`)
} // modalFindDefault

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * close modal box
 * if no key is provided, closes closest .mele-modal
 * @param {string} [key] - identifier of mele-modal which should be closed
 * @returns {null}
 */
Element.prototype.modalClose = function(key) {
  if(key instanceof HTMLElement) {
    key.deleteElement()
  }else {
    key = String(key??'')
    if(key==='') {
      const elModal = this.closest('.mele-modal,.mele-modalwindow')
      if(elModal) elModal.deleteElement()
    }else{
      const body = this.ownerDocument.body
      body.forAll(`>.mele-modal@{${key}}, >.mele-modalwindow@{${key}}`, el=>el.deleteElement())
    }
  }
  return null
} // modalClose

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * close modal dialog with default key
 * default keys are used in modal which opened from another element, i.e. mele-dropdown
 * @returns {null}
 */
Element.prototype.modalCloseDefault = function() {
  return this.modalClose(DEFAULT_MODAL_KEY)
} // modalCloseDefault

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * create modal window
 * @param {(Object|JSON)} json - definition of module structure
 * @example
 * this.modalWindow({
 *   header: "Example",
 *   children: [...]
 * })
 * @returns {mele-modalwindow}
 */
Element.prototype.modalWindow = function(json) {
  json = {
    tag: 'mele-modalwindow',
    key: ':modal',
    ...json,
    invoker: this,
  }

  const key = String(json.key??'')

  // remove existing modal with the same key
  const body = this.ownerDocument.body
  const existing = body.findAll(`>.mele-modal@{${key}}, >.mele-modalwindow@{${key}}`)
  for(const el of existing) {
    el.deleteElement()
  }

  const { children, items, ...core } = json

  // create empty modal element
  const modal = body.appendChildren(core)

  // setup non-attribute properties
  // before adding children, so they can already access them
  // on startup
  const owner = this.closest('.mele-window')
  const ref = owner.getAttribute('mod')
  modal.setAttribute('modal', ref)
  modal.module = owner.module
  modal.publishToModule = owner.publishToModule
  modal.publishTo = owner.publishTo
  modal.publishOpen = owner.publishOpen

  // add children and items
  if(children) modal.appendChildren(children)
  if(items) modal.setItems(items)

  // setup visual attributes after creating children
  // so they obtain update of these attributes
  modal.behavior = owner.behavior
  modal.theme = owner.theme

  return modal
} // modalWindow

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * helper method to create dialog with NO/YES buttons, calling yes_callback() when YES is pressed by user
 * @param {(Object|Object[])} content - modal body content
 * @param {(string|callback)} dialog_title - header text of modal or callback function called when YES is pressed
 * @param {callback} [yes_callback] - callback function called when OK is pressed; if not provided, returns promise
 * @returns {(mele-modal|promise)} if callback is provided, returns dialog object, otherwise returns promise
 */
Element.prototype.modalConfirm = function(content, dialog_title, yes_callback) {
  if(typeof dialog_title==='function') {
    yes_callback = dialog_title
    dialog_title = ''
  }
  if(typeof content==='string' || typeof content==='number') {
    content = {
      tag: 'mele-text',
      label: String(content??''),
    }
  }
  if(!Array.isArray(content)) { content = [content] }

  let dialog_resolve = null
  let promise = null
  if(typeof yes_callback !== 'function') {
    promise = new Promise((resolve)=>{
      dialog_resolve = resolve
    })
  }

  const modal = this.modalOpen({
    key: ':modal_confirm',
    class: 'modaldialog confirm',
    position: 'center',
    header: String(dialog_title??''),
    items: content,
    footerbuttons: ['no', 'yes'],
    eventCancel: ()=>{
      modal.modalClose()
      if(dialog_resolve) {
        dialog_resolve(false)
      }
    },
    eventSubmit: ()=>{
      modal.modalClose()
      if(dialog_resolve) {
        dialog_resolve(true)
      }else{
        yes_callback()
      }
    },
  })

  if(promise) {
    return promise
  }else {
    return modal
  }
} // modalConfirm

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * helper method to create modal dialog to gather user inputs and OK button
 * @param {(Object|Object[])} content - modal body content
 * @param {(string|callback)} dialog_title - header text of modal or callback function called when YES is pressed
 * @param {callback} [callback] - callback function called when OK is pressed; if not provided, returns promise
 * @returns {(mele-modal|promise)} if callback is provided, returns dialog object, otherwise returns promise
 */
Element.prototype.modalPrompt = function(content, dialog_title, callback) {
  if(typeof dialog_title==='function') {
    callback = dialog_title
    dialog_title = ''
  }
  if(typeof content==='string' || typeof content==='number') {
    content = {
      tag: 'mele-text',
      label: String(content??''),
    }
  }
  if(!Array.isArray(content)) { content = [content] }

  let dialog_resolve = null
  let promise = null
  if(typeof callback !== 'function') {
    promise = new Promise((resolve)=>{
      dialog_resolve = resolve
    })
  }

  const modal = this.modalOpen({
    key: ':modal_prompt',
    class: 'modaldialog prompt',
    position: 'center',
    header: String(dialog_title??''),
    items: content,
    footerbuttons: ['cancel', 'ok'],
    outclick: ':modal.cancel',
    focusfirst: true,
    focused: true,
    eventCancel: ()=>{
      modal.modalClose()
      if(dialog_resolve) {
        dialog_resolve()
      }else {
        callback()
      }
    },
    eventSubmit: ()=>{
      const values = modal.getValues()
      modal.modalClose()
      if(dialog_resolve) {
        dialog_resolve(values)
      }else{
        callback(values)
      }
    },
  })

  const first_input = modal.find('mele-input')
  if(first_input) {
    first_input.focus()
  }

  if(promise) {
    return promise
  }else {
    return modal
  }
} // modalPrompt

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * creates modal dialog with spinner and message description
 * @param {string} [message] - text displayed under spinner
 * @param {string} [dialog_title] - header text of modal
 * @returns {mele-modal}
 */
Element.prototype.modalWait = function(message, dialog_title) {
  message = String(message??'')
  const items = message==='' ? [] : [{ tag: 'mele-text', label: message }]
  items.push({
    tag: 'mele-image',
    icon: 'sys.wait',
    class: 'modal-wait',
  })
  const modal = this.modalOpen({
    class: 'modaldialog wait',
    position: 'center',
    header: String(dialog_title??''),
    items,
  })
  return modal
} // modalWait

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * creates info dialog with OK button
 * when pressed OK button, eventSubmit on modal is called
 * if message is empty, dialog will not be shown and will close closest modal in DOM
 * @param {string} [message] - text displayed in center of dialog
 * @param {string} [title] - header text of modal
 * @param callback
 * @returns {mele-modal}
 */
Element.prototype.modalInfo = function(message, title, callback) {
  if(typeof title==='function') {
    callback = title
    title = ''
  }

  let dialog_resolve = null
  let promise = null
  if(typeof callback !== 'function') {
    promise = new Promise((resolve)=>{
      dialog_resolve = resolve
    })
  }

  message = String(message??'')
  if(message==='') return this.modalCloseDefault()
  title = String(title??'')
  if(title==='') title='Info'
  const modal = this.modalOpen({
    class: 'modaldialog info',
    position: 'center',
    outclick: ':modal.cancel',
    header: title,
    items: [{ tag: 'mele-text', label: message }],
    footerbuttons: ['okok'],
    eventCancel: ()=>{
      modal.modalClose()
      dialog_resolve()
    },
    eventSubmit: ()=>{
      modal.modalClose()
      dialog_resolve()
    },
  })

  if(promise) {
    return promise
  }else {
    return modal
  }
} // modalInfo

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * helper method to create error dialog with OK button
 * when pressed OK button, eventSubmit on modal is called
 * if message is empty, dialog will not be shown and will close closest modal in DOM
 * @param {string} [message] - text shown in modal body
 * @param {string} [title] - header text of modal
 * @returns {mele-modal}
 */
Element.prototype.modalError = function(message, title) {
  message = String(message??'')
  if(message==='') return this.modalCloseDefault()
  title = String(title??'')
  if(title==='') title='Error'
  const modal = this.modalOpen({
    class: 'modaldialog error',
    position: 'center',
    outclick: ':modal.cancel',
    header: title,
    items: [{ tag: 'mele-text', label: message }],
    footerbuttons: ['okok'],
    eventSubmit: ()=>{
      modal.modalClose()
    },
  })
  return modal
} // modalError

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * create popup view within parent module.
 * unlinke modalOpen, which creates a dialog relative to entire application popupOpen creates dialog within module
 * @param {(Object|JSON)} json - definition of module structure
 * @returns {mele-popup}
 */
Element.prototype.popupOpen = function(json) {
  const parent = this.closest('.mele-window')
  if(typeof json==='string') json = { key: json }
  if(!json || typeof json !== 'object') return
  let key = String(json.key??'')
  if(key==='') key = json.key = DEFAULT_POPUP_KEY
  parent.forAll(`>.mele-popup@{${key}}`, el=>el.deleteElement())
  json = {
    tag: 'mele-popup',
    ...json,
    invoker: this,
  }
  return parent.appendChildren(json)
} // popupOpen

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * finds popup view within parent module.
 * @param {(Object|JSON)} key - definition of module structure
 * @returns {mele-popup}
 */
Element.prototype.popupFind = function(key) {
  key = String(key??'')
  if(key==='') {
    return this.closest('.mele-popup')
  }else{
    const parent = this.closest('.mele-window')
    if(!parent) return null
    return parent.find(`>.mele-popup@{${key}}`)
  }
} // popupFind

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * close popup view
 * if no key is provided, closes closest .mele-popup
 * @param {string} [key] - identifier of mele-popup which should be closed
 * @returns {null}
 */
Element.prototype.popupClose = function(key) {
  if(key instanceof HTMLElement) {
    key.deleteElement()
  }else {
    key = String(key??'')
    if(key==='') {
      const elPopup = this.closest('.mele-popup')
      if(elPopup) elPopup.deleteElement()
    }else{
      const parent = this.closest('.mele-window')
      parent.forAll(`>.mele-popup@{${key}}`, el=>el.deleteElement())
    }
  }
  return null
} // popupClose

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * close popup dialog with default key
 * default keys are used in popup which opened from another element
 * @returns {null}
 */
Element.prototype.popupCloseDefault = function() {
  return this.popupClose(DEFAULT_POPUP_KEY)
} // popupCloseDefault

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * helper method to create dialog with NO/YES buttons, calling yes_callback() when YES is pressed by user
 * @param {(Object|Object[])} content - popup body content
 * @param {(string|callback)} dialog_title - header text of popup or callback function called when YES is pressed
 * @param {callback} [yes_callback] - callback function called when YES is pressed
 * @returns {mele-popup}
 */
Element.prototype.popupConfirm = function(content, dialog_title, yes_callback) {
  if(typeof dialog_title==='function') {
    yes_callback = dialog_title
    dialog_title = ''
  }
  if(typeof content==='string' || typeof content==='number') {
    content = {
      tag: 'mele-text',
      label: String(content??''),
    }
  }
  if(!Array.isArray(content)) { content = [content] }
  const me = this
  const popup = me.popupOpen({
    class: 'popupdialog confirm',
    position: 'center',
    header: String(dialog_title??''),
    items: content,
    footerbuttons: ['no', 'yes'],
    eventSubmit: ()=>{
      popup.popupClose()
      yes_callback()
    },
  })
  return popup
} // popupConfirm

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * creates popup dialog with spinner and message description
 * @param {string} [message] - text displayed under spinner
 * @param {string} dialog_title - header text of popup
 * @returns {mele-popup}
 */
Element.prototype.popupWait = function(message, dialog_title) {
  message = String(message??'')
  const items = message==='' ? [] : [{ tag: 'mele-text', label: message }]
  items.push({
    tag: 'mele-image',
    icon: 'sys.wait',
    class: 'popup-wait',
  })
  const popup = this.popupOpen({
    class: 'popupdialog wait',
    position: 'center',
    header: String(dialog_title??''),
    items,
  })
  return popup
} // popupWait

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * creates info dialog with OK button
 * when pressed OK button, eventSubmit on popup is called
 * if message is empty, dialog will not be shown and will close closest popup in DOM
 * @param {string} [message] - text displayed in center of dialog
 * @param {string} [title] - header text of popup
 * @returns {mele-popup}
 * */
Element.prototype.popupInfo = function(message, title) {
  message = String(message??'')
  if(message==='') return this.popupCloseDefault()
  title = String(title??'')
  if(title==='') title='Info'
  const popup = this.popupOpen({
    class: 'popupdialog info',
    position: 'center',
    outclick: ':modal.cancel',
    header: title,
    items: [{ tag: 'mele-text', label: message }],
    footerbuttons: ['okok'],
    eventSubmit: ()=>{
      popup.popupClose()
    },
  })
  return popup
} // popupInfo

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * helper method to create error dialog with OK button
 * when pressed OK button, eventSubmit on popup is called
 * if message is empty, dialog will not be shown and will close closest popup in DOM
 * @param {string} [message] - text shown in popup body
 * @param {string} [title] - header text of popup
 * @returns {mele-popup}
 */
Element.prototype.popupError = function(message, title) {
  message = String(message??'')
  if(message==='') return this.popupCloseDefault()
  title = String(title??'')
  if(title==='') title='Error'
  const popup = this.popupOpen({
    class: 'popupdialog error',
    position: 'center',
    outclick: ':modal.cancel',
    header: title,
    items: [{ tag: 'mele-text', label: message }],
    footerbuttons: ['okok'],
    eventSubmit: ()=>{
      popup.popupClose()
    },
  })
  return popup
} // popupError

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * wrapper around msysListener publish method inherited by this element parent .mele-window
 * publishes a MosyMessage to channel #ME
 * @param {string} action - action from mm catalogue
 * @param {Object} [params] - object with action properties based on mm catalogue
 * @example
 * this.publish("mmButtonClicked", {button: this.key, long: true})
 */
Element.prototype.publish = function(action, params) {
  params = _.toPlainObject(params)
  const module_window = this.closest('.mele-window,.mele-modal')
  if(typeof module_window?.publishToModule==='function') {
    module_window.publishToModule(action, params)
  }
} // publish

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * wrapper around msysListener publish method inherited by this element parent .mele-window
 * publishes a MosyMessage to a specific target
 * @param {string} target - target or channel to which message is sent
 * @param {string} action - action from mm catalogue
 * @param {Object} [params] - object with action properties based on mm catalogue
 * @example
 * this.publish("mmodMessages", "mmButtonClicked", {button: "send"})
 */
Element.prototype.publishTo = function(target, action, params) {
  params = _.toPlainObject(params)
  const module_window = this.closest('.mele-window,.mele-modal')
  if(typeof module_window?.publishTo==='function') {
    module_window.publishTo(target, action, params)
  }
} // publishTo

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * wrapper around msysListener publish method inherited by this element parent .mele-window
 * publishes a mmOpen message to a specific target
 * @param {string} target - target or channel to which message is sent
 * @param {string} id - id param of mmOpen message
 */
Element.prototype.publishOpen = function(target, id) {
  const module_window = this.closest('.mele-window,.mele-modal')
  if(typeof module_window?.publishOpen==='function') {
    module_window.publishOpen(target, id)
  }
} // publishOpen

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * getter/setter of language attribute of all elements
 * when calling a setter, method languageChanged is called on each element
 */
Object.defineProperty(Element.prototype, 'language', {
  get: function() {
    return mele_lang
  },
  set: function(value) {
    const old = mele_lang
    if(old===value) return
    mele_lang = value
    this.closest('body').forAll('*', (el)=>{
      if(typeof el.languageChanged==='function') {
        el.languageChanged(value, old)
      }
    })
  },
}) // language

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * props DE/SERIALIZATION METHODS
 */
Object.defineProperty(Element.prototype, 'props', {
  get: function() { return this.getProperties() },
  set: function(value) { return this.setProperties(value) },
}) // props

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * js DE/SERIALIZATION METHODS
 */
Object.defineProperty(Element.prototype, 'js', {
  get: function() { return this.getElementJS() },
  set: function(value) { return this.setElementFromJS(value) },
}) // js

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * json DE/SERIALIZATION METHODS
 */
Object.defineProperty(Element.prototype, 'json', {
  get: function() { return this.getElementJSON() },
  set: function(value) { return this.setElementFromJSON(value) },
}) // json

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * return definition of element and its children in JS object form
 * @returns {Object} - definition of element
 */
Element.prototype.getElementJS = function() {
  const json = this.getProperties()
  json.tag = String(this.tagName).toLowerCase()
  const children = this.getChildren()
  if(children.length>0) { json.children = children }
  return json
} // getElementJS

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * set up element and its children from scratch from definition in JS object form
 * @param {(Object|JSON)} json - definition of element
 */
Element.prototype.setElementFromJS = function(json) {
  if(!json || typeof json!=='object') return
  this.setProperties(json)
  this.setChildren(json.children)
} // setElementFromJS

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * update element (and optionally children) from definition in JS object form
 * @param {(Object|JSON)} json - definition of element
 */
Element.prototype.updateElementFromJS = function(json) {
  if(typeof json==='string' && json.length>0) {
    try{ json = JSON.parse(json) }catch(e) { return }
  }
  if(!json || typeof json !== 'object') return
  this.updateProperties(json)
  if(json.children !== undefined && json.children !== null) {
    this.setChildren(json.children)
  }
} // updateElementFromJS

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * replaces element with children from definition in JS object form
 * this method re-creates the object and is therefore capable of changing tag of the element
 * @param {(Object|JSON)} json - definition of element
 * @returns {HTMLElement}
 */
Element.prototype.replaceElementFromJS = function(json) {
  const el = this.parentElement.appendChildren(json, this)
  this.deleteElement()
  return el
} // replaceElementFromJS

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * deletes element from the parent
 * does not actually delete the object if it is referenced elsewhere
 * @returns {null}
 */
Element.prototype.deleteElement = function() {
  if(this.parentElement) {
    this.parentElement.removeChild(this)
  }
  return null
} // deleteElement

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * return definition of element and its children in JSON string
 * @returns {string} stringified json definition of element
 */
Element.prototype.getElementJSON = function() {
  return JSON.stringify(this.js)
} // getElementJSON

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * set up element and its children from scratch from definition in JSON string
 * @param {(Object|JSON)} json - definition of element
 */
Element.prototype.setElementJSON = function(json) {
  try{
    json = JSON.parse(json)
    this.js = json
  }catch(e) {
  }
} // setElementJSON

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * retrieves properties/attributes of the element
 * @returns {Object} - object of key/val properties of element
 */
Element.prototype.getProperties = function() {
  const json = {}
  const attrs = this.attributes
  for(let i=0; i<attrs.length; i++) {
    json[attrs[i].name] = attrs[i].value
  }
  return json
} // getProperties

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * set properties/attributes of the element
 * @param {(Object|JSON)} json - definition of element
 * @param {boolean} no_removal - if true, prevents removal of obsolete properties
 * @returns {HTMLElement}
 */
Element.prototype.setProperties = function(json, no_removal) {
  if(!json || typeof json !== 'object') return

  // create/replace given attributes
  for(const attr in json) {
    if(typeof json[attr]==='function') {
      this[attr] = json[attr]
    }else if(attr!=='tag' && attr!=='children' && attr!=='html') {
      if(json[attr]===undefined || json[attr]===null) {
        this.removeAttribute(attr)
      }else {
        this.setAttribute(attr, json[attr])
      }
    }
  }

  // remove any obsolete existing attributes
  if(!no_removal) {
    const attrs = this.attributes
    for(let i=0; i<attrs.length; i++) {
      const attr = attrs[i].name
      if(typeof json[attr]==='undefined') {
        this.removeAttribute(attr)
      }
    }
  }

  if(typeof json.html==='string' || typeof json.html==='number') {
    this.innerHTML = json.html
  }

  return this
} // setProperties

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * update properties/attributes of the element
 * @param {(Object|JSON)} json - definition of element
 * @returns {HTMLElement}
 */
Element.prototype.updateProperties = function(json) {
  return this.setProperties(json, true)
} // updateProperties

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * return list of children of element
 * @returns {HTMLElement[]}
 */
Element.prototype.getChildren = function() {
  const children = []
  for(let i=0; i<this.childNodes.length; i++) {
    const node = this.childNodes[i]
    if(node.nodeType===Node.TEXT_NODE) {
      children.push(node.nodeValue)
    }else if(node.nodeType===Node.ELEMENT_NODE) {
      children.push(node.js)
    }
  }
  return children
} // getChildren

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * removes nested children of the element
 */
Element.prototype.deleteChildren = function() {
  this.innerHTML = ''
} // deleteChildren

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * appends children inside of element
 * @param {(Object|Object[])} children - definition of child elements
 * @returns {HTMLElement|HTMLElement[]}
 */
Element.prototype.setChildren = function(children) {
  this.deleteChildren()
  return this.appendChildren(children)
} // setChildren

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * prepends child(ren) before given child (or at the beginning if none specified)
 * @param {(Object|Object[])} children - definition of child(ren)
 * @param {(string|HTMLElement)} before_child - child node/element or string representing child's key
 */
Element.prototype.prependChildren = function(children, before_child) {
  const array = Array.isArray(children)
  if(!array) children = [children]

  if(typeof before_child==='string' || typeof before_child==='number') {
    before_child = this.find('>@{'+before_child+'}')
  }
  if(!before_child || typeof before_child !== 'object') {
    before_child = this.firstChild
  }
  const result = children.map(child => {
    return this._insertChild(child, before_child)
  })
  return(array ? result : result[0])
} // prependChildren

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * appends child(ren) after given child (or at the end if none specified)
 * @param {(Object|Object[])} children - definition of child(ren)
 * @param {(string|HTMLElement)} [after_child] - child node/element or string representing child's key
 */
Element.prototype.appendChildren = function(children, after_child) {
  const array = Array.isArray(children)
  if(!array) children = [children]

  if(typeof after_child==='string' || typeof after_child==='number') {
    after_child = this.find('>@{'+after_child+'}')
  }
  const before_child = after_child?.nextSibling || null
  const result = children.map(child => {
    return this._insertChild(child, before_child)
  })
  return(array ? result : result[0])
} // appendChildren

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * helper method for insert text or element node into the list of element's sub-nodes
 * @param {(JSON|Object|string)} json - definition of child elements or string inserted as TextNode
 * @param {HTMLElement} before_node - child node/element
 */
Element.prototype._insertChild = function(json, before_node) {
  const doc = this.ownerDocument
  if(!json) return
  if(typeof json==='string' || typeof json==='number') {
    // we also allow insertion of plain strings
    return this.insertBefore(doc.createTextNode(json), before_node)
  }else if(typeof json==='object') {
    // but usually we add child element
    if(!/^\w+(-\w+)*$/.test(json.tag)) return null
    const tag = json.tag.toLowerCase()

    json = _.omit(json, ['tag'])

    // create element
    const el = this.createElementFast(tag)

    this.insertBefore(el, before_node||null)
    if(typeof el.connectedCallback==='function') {
      el.connectedCallback('manual after insert') // force synchroneous initialization
    }

    el.updateElementFromJS(json)
    return el
  }
} // _insertChild

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * creates and appends element
 * @param {string} tag - valid tag name for creation of element
 */
Element.prototype.appendElement = function(tag) {
  const el = this.createElementFast(tag)
  return this.appendChild(el)
} // appendElement

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * updates document navigation bar with "#" prefix and url
 * @param {string} hash - encoded URL hash
 */
Element.prototype.updateUrlHash = function(hash) {
  const doc = this.ownerDocument
  const win = doc.defaultView || doc.parentWindow
  if(win?.updateUrlHash) {
    win.updateUrlHash(hash)
  }
} // updateUrlHash

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * sets document navigation bar with "#" prefix and url
 * @param {string} hash - encoded URL hash
 */
Element.prototype.setUrlHash = function(hash) {
  const doc = this.ownerDocument
  const win = doc.defaultView || doc.parentWindow
  if(win?.setUrlHash) {
    win.setUrlHash(hash)
  }
} // setUrlHash

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * creates a print preview in modal popup
 */
Element.prototype.popupPrintPreview = function() {
  const holder = this.closest('mele-window')
  if(holder) {
    const elModal = holder.popupOpen({
      key: ':printpreview',
      position: 'center',
      outclick: ':modal.cancel',
      inclick: ':modal.cancel',
      class: 'modal-preview print',
    })
    elModal._body.appendChild(this.cloneNode(true))
    window.print()
  }
} // popupPrintPreview

DocumentFragment.prototype._insertChild = Element.prototype._insertChild

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * creates preview of image in modal dialog
 * similar to lightbox.js
 */
Image.prototype.modalImagePreview = function() {
  if(!this.src) return
  const holder = this.closest('mele-window')
  if(holder) {
    const elModal = holder.modalOpen({
      key: ':image',
      position: 'center',
      outclick: ':modal.cancel',
      inclick: ':modal.cancel',
      class: 'modal-preview',
    })
    elModal._body.appendChildren({
      tag: 'img',
      src: this.src,
      class: 'preview',
      alt: ' ',
    })
  }
} // modalImagePreview

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * creates preview of image in popup dialog
 * similar to lightbox.js
 */
Image.prototype.popupImagePreview = function() {
  if(!this.src) return
  const holder = this.closest('mele-window')
  if(holder) {
    const elModal = holder.popupOpen({
      key: ':image',
      position: 'center',
      outclick: ':modal.cancel',
      inclick: ':modal.cancel',
      class: 'modal-preview',
    })
    elModal._body.appendChildren({
      tag: 'img',
      src: this.src,
      class: 'preview',
      alt: ' ',
    })
  }
} // popupImagePreview

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * helper method to obtain files from drag & drop
 */
DataTransfer.prototype.getFilesRecursively = async function() {
  const items = [...this.items]
  const files = []

  async function readEntry(entry, path = '') {
    return new Promise((resolve) => {
      if(entry.isFile) {
        entry.file(
          file => {
            file.relativePath = path + file.name
            files.push(file)
            resolve()
          },
          () => {
            resolve() // ignore errors
          },
        )
      }else if(entry.isDirectory) {
        const reader = entry.createReader()
        reader.readEntries(
          async entries => {
            for(const e of entries) {
              await readEntry(e, path + entry.name + '/')
            }
            resolve()
          },
          () => {
            resolve() // ignore errors
          },
        )
      } else {
        resolve()
      }
    })
  }

  if(items[0]?.getAsEntry) {
    for(const item of items) {
      const entry = item.getAsEntry()
      if(entry) await readEntry(entry)
    }
    return files
  }else if(items[0]?.webkitGetAsEntry) {
    for(const item of items) {
      const entry = item.webkitGetAsEntry()
      if(entry) await readEntry(entry)
    }
    return files
  }else {
    // Fallback: flat list
    return [...this.files]
  }
} // getFilesRecursively
